import React, { useEffect, useState } from 'react';
import {
    Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Link,
    MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../common/ConfirmDialog';
import VatTotals from '../common/VatTotals';
import { CategoryTag } from '../common/ui';
import QuoteService from '../../services/QuoteService';
import CategoryService from '../../services/CategoryService';
import { errorMessage, fieldErrors } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { fmtMoney, fmtNumber } from '../../utils/format';

const headSx = { color: '#94A3B8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5, whiteSpace: 'nowrap' };
const EMPTY = { description: '', quantity: '1', unit: '', unitPrice: '', categoryId: '' };
const round2 = (n) => Math.round(n * 100) / 100;

/** Add/edit one position: category, description, quantity + optional unit, unit price, live line total. */
function ItemDialog({ open, item, defaultCategoryId, categories, onClose, onSave }) {
    const { t } = useTranslation();
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const units = t('quotes.items.unitSuggestions').split(',').map(s => s.trim()).filter(Boolean);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(item
            ? { description: item.description, quantity: String(item.quantity), unit: item.unit ?? '', unitPrice: String(item.unitPrice), categoryId: item.category?.id ?? '' }
            : { ...EMPTY, categoryId: defaultCategoryId ?? '' });
    }, [open, item, defaultCategoryId]);

    const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setErrors(x => ({ ...x, [k]: undefined })); };
    const qty = Number(form.quantity), price = Number(form.unitPrice);
    const lineTotal = form.quantity !== '' && form.unitPrice !== '' && !Number.isNaN(qty * price) ? round2(qty * price) : null;

    const save = async () => {
        const v = {};
        if (!form.description.trim()) v.description = t('validation.required');
        if (form.quantity === '' || !(qty > 0)) v.quantity = t('quotes.items.quantityPositive');
        if (form.unitPrice === '') v.unitPrice = t('validation.required');
        else if (price < 0) v.unitPrice = t('jobs.errors.priceNegative');
        setErrors(v);
        if (Object.keys(v).length) return;
        setSaving(true);
        try {
            await onSave({ description: form.description.trim(), quantity: qty, unit: form.unit.trim() || null, unitPrice: price, categoryId: form.categoryId || null });
        } catch (err) {
            const fe = fieldErrors(err);
            if (Object.keys(fe).length) setErrors(fe);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>{item ? t('quotes.items.editTitle') : t('quotes.items.addTitle')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid size={12}>
                        <TextField select fullWidth size="small" label={t('jobs.fields.category')} value={form.categoryId} onChange={set('categoryId')}
                                   helperText={t('quotes.items.categoryHint')}>
                            <MenuItem value=""><em>{t('jobs.noCategory')}</em></MenuItem>
                            {categories.map(c => (
                                <MenuItem key={c.id} value={c.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: c.color }} />{c.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={12}>
                        <TextField fullWidth size="small" required autoFocus multiline maxRows={4} label={t('quotes.items.description')}
                                   placeholder={t('quotes.items.descriptionPlaceholder')} value={form.description} onChange={set('description')}
                                   error={!!errors.description} helperText={errors.description} slotProps={{ htmlInput: { maxLength: 500 } }} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField fullWidth size="small" required type="number" label={t('quotes.items.quantity')} value={form.quantity}
                                   onChange={set('quantity')} error={!!errors.quantity} helperText={errors.quantity}
                                   slotProps={{ htmlInput: { min: 0.01, step: 'any' } }} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Autocomplete freeSolo options={units} value={form.unit} inputValue={form.unit}
                                      onInputChange={(_, v) => setForm(f => ({ ...f, unit: v.slice(0, 20) }))}
                                      renderInput={(p) => <TextField {...p} size="small" label={t('quotes.items.unit')} />} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                        <TextField fullWidth size="small" required type="number" label={t('quotes.items.unitPrice')} value={form.unitPrice}
                                   onChange={set('unitPrice')} error={!!errors.unitPrice} helperText={errors.unitPrice}
                                   slotProps={{ input: { startAdornment: <InputAdornment position="start">€</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }} />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 1, mt: 2.5 }}>
                    <Typography sx={{ fontSize: 13, color: '#64748B' }}>{t('quotes.items.lineTotal')}:</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }}>{fmtMoney(lineTotal)}</Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving} disableElevation sx={{ minWidth: 120 }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : (item ? t('common.save') : t('quotes.items.add'))}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * The quote's positions, laid out like Plankton CRM's quote page: numbered table, row actions on
 * hover (always visible on touch screens), add/edit in a dialog, delete with confirmation, total below.
 */
export default function QuoteItemsCard({ quote, money, editable, onChange }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [dialog, setDialog] = useState(null);     // { item } or {} for a new one
    const [toDelete, setToDelete] = useState(null);
    const [busy, setBusy] = useState(false);
    const [categories, setCategories] = useState([]);
    const items = quote.items || [];
    useEffect(() => { if (editable) CategoryService.list().then(setCategories).catch(() => {}); }, [editable]);
    // A new position starts in the category of the last one, since positions of one kind are usually added together.
    const lastCategoryId = items.length ? items[items.length - 1].category?.id : undefined;

    // The header update needs every header field; only the PDV switch changes.
    const toggleVat = (on) => {
        setBusy(true);
        run(() => QuoteService.update(quote.id, {
            clientId: quote.client.id, title: quote.title, description: quote.description,
            parcelNumber: quote.parcelNumber, cadastralZone: quote.cadastralZone, municipality: quote.municipality,
            ownerName: quote.ownerName, coOwners: quote.coOwners, propertySheet: quote.propertySheet, vatEnabled: on,
        }), on ? 'vat.toast.on' : 'vat.toast.off').catch(() => {}).finally(() => setBusy(false));
    };

    const run = async (call, toastKey) => {
        try {
            onChange(await call());
            if (toastKey) showToast(t(toastKey), 'success');
            return true;
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
            throw err;
        }
    };

    // Positions carry prices: unlock first. The dialog then edits the reloaded item (with its price).
    const openDialog = async (d) => { if (await money.ensure()) setDialog(d); };
    const dialogItem = dialog?.item ? items.find(i => i.id === dialog.item.id) : undefined;

    const save = async (payload) => {
        const item = dialog?.item;
        await run(() => item ? QuoteService.updateItem(quote.id, item.id, payload) : QuoteService.addItem(quote.id, payload),
            item ? 'quotes.items.updated' : 'quotes.items.added');
        setDialog(null);
    };

    const remove = async () => {
        try { await run(() => QuoteService.deleteItem(quote.id, toDelete.id), 'quotes.items.deleted'); } catch { /* toast shown */ }
        setToDelete(null);
    };

    const move = async (item, direction) => {
        setBusy(true);
        try { await run(() => QuoteService.moveItem(quote.id, item.id, direction)); } catch { /* toast shown */ }
        setBusy(false);
    };

    const addButton = (
        <Button size="small" startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />} onClick={() => openDialog({})}
                sx={{ fontWeight: 600, fontSize: 13, px: 1.5, '&:hover': { bgcolor: '#F0F7FF' } }}>
            {t('quotes.items.add')}
        </Button>
    );

    return (
        <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px', bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9', minHeight: 52 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a2332' }}>
                    {t('quotes.items.title')}
                    {items.length > 0 && <Typography component="span" sx={{ ml: 1, fontSize: 12, color: '#94A3B8', fontWeight: 400 }}>({items.length})</Typography>}
                </Typography>
                {editable && addButton}
            </Box>

            <TableContainer>
                <Table size="small" sx={{ minWidth: 560 }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#FAFBFC' }}>
                            <TableCell sx={{ ...headSx, width: 40 }}>#</TableCell>
                            <TableCell sx={headSx}>{t('quotes.items.description')}</TableCell>
                            <TableCell sx={headSx} align="right">{t('quotes.items.quantity')}</TableCell>
                            <TableCell sx={headSx} align="right">{t('quotes.items.unitPrice')}</TableCell>
                            <TableCell sx={headSx} align="right">{t('quotes.items.total')}</TableCell>
                            {editable && <TableCell sx={{ width: 132 }} />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={editable ? 6 : 5} align="center" sx={{ py: 5, borderBottom: 0 }}>
                                    <Typography sx={{ color: '#94A3B8', fontSize: 14, mb: editable ? 1.5 : 0 }}>{t('quotes.items.empty')}</Typography>
                                    {editable && <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openDialog({})}>{t('quotes.items.addFirst')}</Button>}
                                </TableCell>
                            </TableRow>
                        ) : items.map((item, idx) => (
                            <TableRow key={item.id} sx={{
                                '&:hover': { bgcolor: '#FAFBFC' },
                                '@media (hover: hover)': { '& .row-actions': { opacity: 0 }, '&:hover .row-actions, &:focus-within .row-actions': { opacity: 1 } },
                                '& .row-actions': { transition: 'opacity 0.15s' },
                            }}>
                                <TableCell sx={{ color: '#94A3B8', fontSize: 13, verticalAlign: 'top', pt: 1.5 }}>{idx + 1}</TableCell>
                                <TableCell sx={{ fontSize: 13.5, fontWeight: 500, color: '#1E293B', wordBreak: 'break-word', verticalAlign: 'top', pt: 1.5 }}>
                                    <Box sx={{ whiteSpace: 'pre-wrap' }}>{item.description}</Box>
                                    {(item.category || item.job) && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                            {item.category && <CategoryTag category={item.category} />}
                                            {item.job && (
                                                <Link component={RouterLink} to={`/jobs/${item.job.id}`} underline="hover" sx={{ fontSize: 12, fontWeight: 600, color: '#059669' }}>
                                                    {item.job.number ? t('quotes.items.inJob', { number: item.job.number }) : t('quotes.items.inJobNoNumber')}
                                                </Link>
                                            )}
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: 13, color: '#475569', whiteSpace: 'nowrap', verticalAlign: 'top', pt: 1.5 }}>
                                    {fmtNumber(item.quantity)}{item.unit ? ` ${item.unit}` : ''}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: 13, color: '#475569', whiteSpace: 'nowrap', verticalAlign: 'top', pt: 1.5 }}>{money.fmt(item.unitPrice)}</TableCell>
                                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', verticalAlign: 'top', pt: 1.5 }}>{money.fmt(item.total)}</TableCell>
                                {editable && (
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                                        <Box className="row-actions" sx={{ display: 'inline-flex' }}>
                                            <Tooltip title={t('quotes.items.moveUp')}><span>
                                                <IconButton size="small" disabled={busy || idx === 0} onClick={() => move(item, -1)} sx={{ color: '#94A3B8' }}><ArrowUpwardIcon sx={{ fontSize: 15 }} /></IconButton>
                                            </span></Tooltip>
                                            <Tooltip title={t('quotes.items.moveDown')}><span>
                                                <IconButton size="small" disabled={busy || idx === items.length - 1} onClick={() => move(item, 1)} sx={{ color: '#94A3B8' }}><ArrowDownwardIcon sx={{ fontSize: 15 }} /></IconButton>
                                            </span></Tooltip>
                                            {!item.job && <Tooltip title={t('common.edit')}>
                                                <IconButton size="small" onClick={() => openDialog({ item })} sx={{ color: '#94A3B8', '&:hover': { color: '#427898', bgcolor: '#F0F7FF' } }}><EditOutlinedIcon sx={{ fontSize: 16 }} /></IconButton>
                                            </Tooltip>}
                                            {!item.job && <Tooltip title={t('common.delete')}>
                                                <IconButton size="small" onClick={() => setToDelete(item)} sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
                                            </Tooltip>}
                                        </Box>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {items.length > 0 && (
                <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #F1F5F9' }}>
                    {/* PDV is part of the quote header, which is frozen once jobs exist. */}
                    <VatTotals doc={{ ...quote, net: quote.price }} fmt={money.fmt}
                               onToggle={editable ? toggleVat : undefined} disabled={busy || !money.visible || quote.jobs.length > 0} />
                </Box>
            )}

            <ItemDialog open={!!dialog && money.visible && (!dialog.item || dialogItem?.unitPrice != null)} item={dialogItem} defaultCategoryId={lastCategoryId} categories={categories} onClose={() => setDialog(null)} onSave={save} />
            <ConfirmDialog open={!!toDelete} title={t('quotes.items.deleteTitle')}
                           text={t('quotes.items.deleteText', { name: toDelete?.description })}
                           onConfirm={remove} onClose={() => setToDelete(null)} />
        </Paper>
    );
}
